import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, CornerDownLeft, Loader2, Sparkles } from "lucide-react";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { quickSearch } from "@/lib/api";
import { archiveId } from "@/lib/archive";

// Fast topic finder (Cmd/Ctrl+K). Server-side search by title/theme/keyword,
// debounced; cmdk's built-in filtering is disabled since results come from the API.
export function QuickSearch({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const navigate = useNavigate();
  const timer = useRef(null);
  const reqId = useRef(0);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) { setQ(""); setItems([]); setLoading(false); setSelected(""); }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) { setItems([]); setLoading(false); return; }
    setLoading(true);
    const id = ++reqId.current;
    timer.current = setTimeout(() => {
      quickSearch(term)
        .then((r) => { if (id === reqId.current) setItems(r.items || []); })
        .catch(() => { if (id === reqId.current) setItems([]); })
        .finally(() => { if (id === reqId.current) setLoading(false); });
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  // Whenever results change, highlight the top match and scroll the list back
  // to the top — otherwise cmdk keeps its old selection and the list appears
  // stuck mid-scroll (you have to scroll up to see the best matches).
  useEffect(() => {
    const first = items[0]?.slug || (q.trim().length >= 2 ? "__ask_archivist" : "");
    setSelected(first);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [items, q]);

  const go = (slug) => { onOpenChange(false); navigate(`/topic/${slug}`); };
  const askArchivist = () => { const term = q.trim(); onOpenChange(false); navigate(`/search?q=${encodeURIComponent(term)}`); };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false} value={selected} onValueChange={setSelected}>
      <CommandInput
        value={q}
        onValueChange={setQ}
        placeholder="Search the Archive by title, theme, or keyword…"
        data-testid="quick-search-input"
      />
      <CommandList data-testid="quick-search-results" ref={listRef}>
        {loading ? (
          <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin text-gold-light" /> Searching the Codex…</div>
        ) : null}
        {!loading && q.trim().length >= 2 && items.length === 0 ? (
          <CommandEmpty>No case files match “{q.trim()}”.</CommandEmpty>
        ) : null}
        {!loading && q.trim().length < 2 ? (
          <div className="px-4 py-6 text-sm text-muted-foreground/70">Type at least 2 characters to find a Case File.</div>
        ) : null}
        {items.length ? (
          <CommandGroup heading="Case Files">
            {items.map((t) => (
              <CommandItem key={t.slug} value={t.slug} onSelect={() => go(t.slug)} data-testid={`quick-search-item-${t.slug}`} className="flex items-start gap-3">
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-gold-light/70" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-foreground">{t.title}</span>
                    <span className="codex-archive-id shrink-0">{archiveId(t.slug)}</span>
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{t.category}</div>
                </div>
                <CornerDownLeft className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        {q.trim().length >= 2 ? (
          <CommandGroup heading="The Archivist">
            <CommandItem value="__ask_archivist" onSelect={askArchivist} data-testid="quick-search-ask-archivist">
              <Sparkles className="mr-2 h-4 w-4 text-gold-light" /> Ask the Archivist about “{q.trim()}”
            </CommandItem>
          </CommandGroup>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}
